import { zodResolver } from "@hookform/resolvers/zod";
import {
  getAllocationRuleGraphAndUseRetrieveQueryKey,
  useAllocationRuleGraphActivate,
  useAllocationRuleGraphAndUseUpsert,
  useAllocationRuleGraphUseActivate,
} from "gen/endpoints/allocation-rule-graph/allocation-rule-graph";
import { getAllocationRuleGraphTableDataRetrieveQueryKey } from "gen/endpoints/allocation-rule-graph-table/allocation-rule-graph-table";
import { getAllocationRuleGraphVersionBasisPreviewTableDataRetrieveQueryKey } from "gen/endpoints/allocation-rule-graph-version/allocation-rule-graph-version";
import { useFundFamilyEventDelete } from "gen/endpoints/fund-family-event/fund-family-event";
import i18n from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useToast } from "shared/components/toast/useToast";
import { rootRoute } from "shared/navigation/Routes";
import { tryParseDecimal } from "shared/utils/DecimalUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { z as zod } from "zod";

export type EdgeOverrideAmount = {
  securityOwnershipId: string;
  overrideAmount: string;
};

export type AllocationRuleGraphUseFormFields = {
  name: string;
  transactionCodeId?: string;
  investmentId?: string;
  calculationId?: string;
  edgeOverrideAmounts?: EdgeOverrideAmount[];
  effectiveDate?: string;
  reasonForChange?: string;
};

export type AllocationRuleGraphUpsertResponse = {
  graphId: string;
  graphVersionId: string;
  graphUseId: string;
  effectiveDate: string;
  graphfundFamilyEventId?: string;
  useFundFamilyEventId?: string;
};

const resolver = (isDateRequired: boolean) =>
  zod
    .object({
      name: zod.string().min(1),
      investmentId: zod.string().optional(),
      calculationId: zod.string().optional(),
      transactionCodeId: zod.string().optional(),
      edgeOverrideAmounts: zod
        .array(
          zod.object({
            securityOwnershipId: zod.string(),
            overrideAmount: zod.string(),
          }),
        )
        .optional(),
      effectiveDate: isDateRequired
        ? zod.string().min(1)
        : zod.string().optional(),
      reasonForChange: isDateRequired // require reason for change on edits, where date is also required
        ? zod.string().min(1)
        : zod.string().optional(),
    })
    .superRefine((values, ctx) => {
      if (!values.investmentId && !values.transactionCodeId) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: i18n.t("errors:required"),
          path: ["investmentId", "transactionCodeId"],
        });
      }

      if (!values.calculationId && !values.edgeOverrideAmounts) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: i18n.t("errors:required"),
          path: ["calculation_id", "edge_override_amounts"],
        });
      }
    });

export const useAllocationRuleGraphForm = (
  fundFamilyId: string,
  isDateRequired: boolean,
  initialValues?: AllocationRuleGraphUseFormFields,
  initialData?: AllocationRuleGraphUpsertResponse,
  onDiscardSuccess?: () => void,
) => {
  const { showToast, showErrorToast } = useToast();
  const form = useForm<AllocationRuleGraphUseFormFields>({
    resolver: zodResolver(resolver(isDateRequired)),
    defaultValues: initialValues,
  });

  const { t } = useTranslation(["allocation"]);
  const invalidateQueries = useInvalidateQueryKeys();

  const navigate = useNavigate();
  const [ruleGraphData, setRuleGraphData] = useState<
    AllocationRuleGraphUpsertResponse | undefined
  >(initialData);
  const [isCustomRuleGraph, setIsCustomRuleGraph] = useState(false);

  const { mutateAsync: upsertRuleGraphAndUse } =
    useAllocationRuleGraphAndUseUpsert({
      mutation: {
        onError: showErrorToast,
        onSuccess: (data) => {
          // this resets the form to "clean", but with the same data
          const formValues = form.getValues();
          form.reset(formValues);

          setRuleGraphData({
            graphId: data.base_model_id,
            graphVersionId: data.id,
            graphUseId: data.allocation_rule_graph_use_id,
            effectiveDate: data.effective_date,
            graphfundFamilyEventId: data.graph_fund_family_event_id,
            useFundFamilyEventId: data.use_fund_family_event_id,
          });
        },
      },
    });

  const { mutateAsync: promoteGraphToActive } =
    useAllocationRuleGraphActivate();

  const { mutateAsync: promoteGraphUseToActive } =
    useAllocationRuleGraphUseActivate();

  const { mutateAsync: deleteFundFamilyEvent } = useFundFamilyEventDelete({
    mutation: {
      onSuccess: () =>
        onDiscardSuccess ? onDiscardSuccess() : toFundStructurePage(),
    },
  });

  const formFieldsToUpsertRequestBody = useCallback(
    async (data: AllocationRuleGraphUseFormFields) => {
      const isValid = await form.trigger();
      if (isValid) {
        return {
          graph_upsert_data: {
            id: ruleGraphData?.graphId ?? undefined,
            name: data.name,
            default_transaction_calculation_id: data.calculationId
              ? data.calculationId
              : undefined,
            fund_family_id: fundFamilyId,
            fund_family_event_id:
              ruleGraphData?.graphfundFamilyEventId ?? undefined,
          },
          use_upsert_data: {
            id: ruleGraphData?.graphUseId ?? undefined,
            transaction_code_id: data.transactionCodeId
              ? data.transactionCodeId
              : undefined,
            investment_id: data.investmentId ? data.investmentId : undefined,
            fund_family_id: fundFamilyId,
            allocation_rule_graph_id: ruleGraphData?.graphId ?? undefined,
            fund_family_event_id:
              ruleGraphData?.useFundFamilyEventId ?? undefined,
          },
          edge_override_amounts:
            data.edgeOverrideAmounts?.map((override: EdgeOverrideAmount) => ({
              security_ownership_id: override.securityOwnershipId,
              override_amount: override.overrideAmount,
            })) ?? [],
          effective_date:
            data.effectiveDate ?? ruleGraphData?.effectiveDate ?? undefined,
        };
      }

      if (form.formState.errors.investmentId) {
        showToast({
          message: t("allocation:ruleGraphEdit.useRequiredError"),
          variant: "error",
        });
      }
    },
    [showToast, t, form, fundFamilyId, ruleGraphData],
  );

  const upsertDraftGraphAndUse = useCallback(async () => {
    const data = form.getValues();
    const validatedData = await formFieldsToUpsertRequestBody(data);

    if (!validatedData) {
      return;
    }

    await upsertRuleGraphAndUse({
      fundFamilyId,
      data: {
        ...validatedData,
      },
    });

    if (ruleGraphData?.graphVersionId) {
      await invalidateQueries(
        getAllocationRuleGraphVersionBasisPreviewTableDataRetrieveQueryKey(
          fundFamilyId,
          ruleGraphData.graphVersionId,
        ),
        getAllocationRuleGraphVersionBasisPreviewTableDataRetrieveQueryKey(
          fundFamilyId,
          ruleGraphData.graphVersionId,
          {
            include_opt_out_badge: true,
            group_by_investor: true,
          },
        ),
      );
    }
  }, [
    formFieldsToUpsertRequestBody,
    fundFamilyId,
    form,
    upsertRuleGraphAndUse,
    ruleGraphData,
    invalidateQueries,
  ]);

  const onFinalize = useCallback(async () => {
    if (
      !(
        ruleGraphData?.graphfundFamilyEventId &&
        ruleGraphData.useFundFamilyEventId
      )
    ) {
      throw new Error(
        "graphfundFamilyEventId and useFundFamilyEventId are required to finalize the graph + use",
      );
    }
    await promoteGraphToActive({
      fundFamilyId,
      allocationRuleGraphId: ruleGraphData.graphId,
    });
    await promoteGraphUseToActive({
      fundFamilyId,
      allocationRuleGraphUseId: ruleGraphData.graphUseId,
      params: {
        should_add_lookups: true,
      },
    });
    await invalidateQueries(
      getAllocationRuleGraphAndUseRetrieveQueryKey(
        fundFamilyId,
        ruleGraphData.graphId,
        ruleGraphData.graphUseId,
      ),
      getAllocationRuleGraphTableDataRetrieveQueryKey(fundFamilyId),
    );
  }, [
    fundFamilyId,
    promoteGraphToActive,
    promoteGraphUseToActive,
    ruleGraphData,
    invalidateQueries,
  ]);

  const onSaveEdgeWeightOverrides = useCallback(
    async (edgeOverrides: EdgeOverrideAmount[]) => {
      form.setValue("edgeOverrideAmounts", edgeOverrides);

      const isCustomRule = edgeOverrides.some(
        (override: EdgeOverrideAmount) =>
          !tryParseDecimal(override.overrideAmount)?.isZero(),
      );
      setIsCustomRuleGraph(isCustomRule);

      if (isCustomRule) {
        form.setValue("calculationId", undefined);
      }
      await upsertDraftGraphAndUse();
    },
    [upsertDraftGraphAndUse, form],
  );

  const toFundStructurePage = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fundStructure({}).$,
    );
  }, [fundFamilyId, navigate]);

  const onCancel = useCallback(async () => {
    if (
      ruleGraphData?.graphfundFamilyEventId &&
      ruleGraphData.useFundFamilyEventId
    ) {
      await deleteFundFamilyEvent({
        fundFamilyId,
        fundFamilyEventId: ruleGraphData.graphfundFamilyEventId,
      });
      await deleteFundFamilyEvent({
        fundFamilyId,
        fundFamilyEventId: ruleGraphData.useFundFamilyEventId,
      });
    } else if (onDiscardSuccess) {
      onDiscardSuccess();
    } else {
      toFundStructurePage();
    }
  }, [
    deleteFundFamilyEvent,
    ruleGraphData,
    fundFamilyId,
    toFundStructurePage,
    onDiscardSuccess,
  ]);

  // only show refresh button if change is meaningful to version - not just effective date or reason for change
  const shouldRefresh = useMemo(
    () =>
      form.formState.isDirty &&
      (!!form.formState.dirtyFields.calculationId ||
        !!form.formState.dirtyFields.edgeOverrideAmounts ||
        !!form.formState.dirtyFields.investmentId ||
        !!form.formState.dirtyFields.transactionCodeId ||
        !!form.formState.dirtyFields.name),
    [form.formState],
  );

  return {
    form,
    ruleGraphData,
    onSaveEdgeWeightOverrides,
    upsertDraftGraphAndUse,
    onFinalize,
    onCancel,
    toFundStructurePage,
    isCustomRuleGraph,
    shouldRefresh,
  };
};
